import React, { useState } from 'react'
import type { CheckInMark } from '../../../hooks/useCheckIns'
import { fbt } from 'fbtee'
import { GoingTipBody } from '../../../components/GoingTipBody'
import { InterestCard } from '../../../components/InterestCard'
import type { SetDetailTab } from '../../../components/SetDetailDialog'
import { OneShotTip, type OneShotTipDismissReason } from '../../../components/OneShotTip'
import { cn } from '../../../lib/utils'
import { setImages } from '../../../lib/setImages'
import { setLabel } from '../../../lib/setLabel'
import { useTimeFormat } from '../../../hooks/useTimeFormat'
import { SetTrimHandles } from './SetTrimHandles'
import { TrimConflictTipBody } from './TrimConflictTipBody'
import { clashStripe, type ClashSpan } from './useTrimConflictTip'
import { useHistoryState } from '../../../hooks/useHistoryState'
import { useSetBlockModel } from './useSetBlockModel'
import { SetNameContent } from '../../../components/SetNameContent'
import { SetBlockDetail } from './SetBlockDetail'

import type { SetFeed } from '../../../hooks/useSetPosts'
import { FeedBadge } from '../../../components/FeedBadge'
import type { TotemMarking } from '../../../hooks/useTotemSlots'
import type { PartialRangeEntryPoint, SetInterestEntryPoint } from '../../../lib/analytics'
import type { Set as FestivalSet, Artist, PartialRange, UserProfile } from '../../../types'

export const SetBlock = React.memo(function SetBlock({
  set,
  registerBlock,
  highlighted = false,
  dimmed = false,
  updated = false,
  gridColumn,
  gridRow,
  artists,
  interestedSetIds,
  goingSetIds,
  onCycleSet,
  interestedMemberIds,
  goingMemberIds,
  crowdMax,
  ownPartialRange,
  memberRanges,
  memberCheckIns,
  onUpdateOwnRange,
  userProfiles,
  currentUserId,
  showGoingTip = false,
  onDismissGoingTip,
  clashesWith,
  clashSpans,
  onClashVisible,
  showClashTip = false,
  onDismissClashTip,
  feed,
  showFeedBadge = true,
  totem,
  onOpenMember,
  defaultTab = 'details',
}: {
  set: FestivalSet
  registerBlock?: (id: string, el: HTMLDivElement | null) => void
  highlighted?: boolean
  /** Doesn't match the active schedule filter — faded back but still tappable. */
  dimmed?: boolean
  /** Moved since the user last opened the schedule — shows the Updated chip. */
  updated?: boolean
  gridColumn: number
  gridRow: string
  artists: Record<string, Artist>
  interestedSetIds: Set<string>
  goingSetIds: Set<string>
  onCycleSet: (setId: string, entryPoint: SetInterestEntryPoint) => void
  interestedMemberIds: string[]
  goingMemberIds: string[]
  crowdMax?: number
  ownPartialRange?: PartialRange | null
  memberRanges?: Record<string, PartialRange>
  memberCheckIns?: Record<string, CheckInMark>
  onUpdateOwnRange?: (setId: string, partialRange: PartialRange | null, entryPoint?: PartialRangeEntryPoint) => void
  userProfiles: Record<string, UserProfile>
  currentUserId?: string | null
  showGoingTip?: boolean
  onDismissGoingTip?: (via: OneShotTipDismissReason) => void
  /** Name of the first other Going set this one overlaps, if any. Drives the one-shot clash tip. */
  clashesWith?: string
  /** The stretches of this card that overlap another Going set — drawn as the persistent clash stripe. */
  clashSpans?: ClashSpan[]
  onClashVisible?: (setId: string) => void
  showClashTip?: boolean
  onDismissClashTip?: (via: OneShotTipDismissReason) => void
  feed?: SetFeed
  showFeedBadge?: boolean
  totem?: TotemMarking
  onOpenMember?: (uid: string) => void
  defaultTab?: SetDetailTab
}) {
  const [heartEl, setHeartEl] = useState<HTMLElement | null>(null)
  const [detail, setDetail] = useHistoryState<SetDetailTab | null>(`set:${set.id}`, null)
  const {
    cardEl,
    setBlockRef,
    trimOverlayEl,
    trimBoxEl,
    status,
    durationMin,
    canTrim,
    trim,
    trimming,
    stopTrim,
    startTrim,
    partial,
    compact,
    nameLines,
  } = useSetBlockModel({
    set,
    registerBlock,
    interestedSetIds,
    goingSetIds,
    ownPartialRange,
    onUpdateOwnRange,
    clashesWith,
    onClashVisible,
    showClashTip,
    onDismissClashTip,
  })

  const { formatClockRange } = useTimeFormat()
  const openDetail = (tab: SetDetailTab) => setDetail(tab)
  const summary = feed?.summaryBySet.get(set.id)
  const someUnloaded = set.artistIds.some(id => !artists[id])
  const { name: artistNames, title: setTitle } = setLabel(set, artists)
  const showB2bNames = someUnloaded || (set.artistIds.length > 1 && artistNames !== set.title?.trim())
  const { imageUrl: artistImageUrl, focal: artistFocal, imageUrls: artistImageUrls, imageFocals: artistImageFocals, seed: cardSeed } = setImages(set, artists)
  // The set's real times, even when the viewer only catches part of it — the
  // window's border already says which part.
  const subtitle = formatClockRange(set.startTime, set.endTime)
  const hasNameClick = feed && !someUnloaded
  const toggle = () => {
    if (showGoingTip) onDismissGoingTip?.('tap')
    onCycleSet(set.id, 'card')
  }

  const placedTotems = totem?.slotsBySet.get(set.id)
  const totemFacepile = placedTotems && placedTotems.length > 0
    ? placedTotems.map(p => ({ emoji: p.totem.emoji, imageUrl: p.totem.imageUrl, borderStyle: p.totem.borderStyle, partial: !!p.slot.partialRange }))
    : undefined

  const feedAriaLabel = fbt('Open set feed', 'Accessible label for the set feed badge on a set card')
  const openFeed = feed && showFeedBadge ? () => openDetail('feed') : undefined
  const feedBadge = openFeed ? <FeedBadge summary={summary} onOpen={openFeed} ariaLabel={feedAriaLabel} /> : null

  const card = (
    <InterestCard
      ref={setBlockRef}
      imageUrl={artistImageUrl}
      imageFocal={artistFocal}
      imageUrls={artistImageUrls}
      imageFocals={artistImageFocals}
      seed={cardSeed}
      heartRef={setHeartEl}
      compact={compact}
      nameLines={nameLines}
      crowdMax={crowdMax}
      status={status}
      interestedMemberIds={interestedMemberIds}
      goingMemberIds={goingMemberIds}
      userProfiles={userProfiles}
      currentUserId={currentUserId}
      onToggle={toggle}
      onNameClick={trimming ? stopTrim : hasNameClick ? () => openDetail(defaultTab) : undefined}
      onLongPress={canTrim && !trimming ? startTrim : undefined}
      onOpenDetail={feed ? openDetail : undefined}
      historyKey={`set:${set.id}`}
      totemFacepile={totemFacepile}
      memberRanges={memberRanges}
      memberCheckIns={memberCheckIns}
      badge={feedBadge}
      nameContent={showB2bNames ? <SetNameContent artistIds={set.artistIds} title={set.collabArtistId ? artistNames : undefined} artists={artists} compact={compact} nameLines={nameLines} /> : undefined}
      activeWindow={partial ? { top: partial.start / durationMin, height: (partial.end - partial.start) / durationMin } : undefined}
      dimContent={trimming}
      style={{
        gridColumn,
        gridRow,
        ...highlighted && { '--highlight-color': status === 'going' ? 'var(--going)' : status === 'interested' ? 'var(--interested)' : undefined },
        ...clashSpans && { '--clash-stripe': clashStripe(clashSpans) },
      } as React.CSSProperties}
      className={cn('m-0.5', dimmed && !highlighted && !trimming && 'opacity-30 grayscale hover:opacity-60', highlighted && 'relative z-30 animate-set-highlight', trimming && 'relative z-30 shadow-xl shadow-black/60 hover:translate-y-0', clashSpans && "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-20 before:w-[3px] before:[background-image:var(--clash-stripe)] before:content-['']")}
      name={artistNames}
      tagline={setTitle}
      subtitle={subtitle}
      updated={updated}
    />
  )

  return (
    <>
      {card}
      {trimming && status !== 'none' && (
        // Shares the card's grid cell. `inset-px` matches the card's 1px border,
        // which is the box the range window is laid out in.
        <div ref={trimOverlayEl} style={{ gridColumn, gridRow }} className="pointer-events-none relative z-40 m-0.5">
          <div ref={trimBoxEl} className="absolute inset-px">
            <SetTrimHandles trim={trim} duration={durationMin} setStartTime={set.startTime} status={status} />
          </div>
        </div>
      )}
      {showGoingTip && (
        <OneShotTip
          open
          onDismiss={via => onDismissGoingTip?.(via)}
          anchor={heartEl}
          side="top"
          body={<GoingTipBody />}
        />
      )}
      {showClashTip && clashesWith && !trimming && (
        <OneShotTip
          open
          onDismiss={via => onDismissClashTip?.(via)}
          anchor={cardEl.current}
          side="top"
          body={<TrimConflictTipBody clashesWith={clashesWith} />}
        />
      )}
      {feed && (
        <SetBlockDetail
          set={set}
          artists={artists}
          artistNames={artistNames}
          setTitle={setTitle}
          status={status}
          detail={detail}
          setDetail={setDetail}
          feed={feed}
          totem={totem}
          placedTotems={placedTotems}
          onOpenMember={onOpenMember}
          interestedMemberIds={interestedMemberIds}
          goingMemberIds={goingMemberIds}
          ownPartialRange={ownPartialRange}
          memberRanges={memberRanges}
          memberCheckIns={memberCheckIns}
          onUpdateOwnRange={onUpdateOwnRange}
          onCycleSet={onCycleSet}
          userProfiles={userProfiles}
          currentUserId={currentUserId}
        />
      )}
    </>
  )
})
